import { Box } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import { type ClientListItemDto, ClientStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { EntityStatusChip } from "Shared/Components/EntityStatusChip";
import { DetailActionCell } from "Shared/DataTable/DetailActionCell";
import { NameLinkCell } from "Shared/NameLinkCell";
import { getFormattedValue, phoneMask } from "Shared/Utils/Format/object";

const columnHelper = createColumnHelper<ClientListItemDto>();

export const CLIENTS_SORTABLE_COLUMNS = [
  "companyName",
  "partnerName",
  "projectCount",
  "email",
  "phone",
] as const;

const isSortable = (id: string) =>
  (CLIENTS_SORTABLE_COLUMNS as readonly string[]).includes(id);

export function useClientsTableColumns(isSysAdmin: boolean) {
  const { t } = useResource();

  return useMemo(
    () => [
      columnHelper.accessor("companyName", {
        header: t(Resources.Clients.CompanyName),
        enableSorting: isSortable("companyName"),
        cell: (info) => (
          <NameLinkCell
            path={AppRouting.ClientDetail.path(info.row.original.clientID)}
          >
            {info.getValue()}
          </NameLinkCell>
        ),
      }),
      ...(isSysAdmin
        ? [
            columnHelper.accessor("partnerName", {
              header: t(Resources.Clients.Partner),
              enableSorting: isSortable("partnerName"),
              cell: (info) => <Box>{info.getValue()}</Box>,
            }),
          ]
        : []),
      columnHelper.accessor("projectCount", {
        header: t(Resources.Clients.ProjectCount),
        enableSorting: isSortable("projectCount"),
        cell: (info) => <Box>{info.getValue()}</Box>,
      }),
      columnHelper.accessor("email", {
        header: t(Resources.Clients.Email),
        enableSorting: isSortable("email"),
        cell: (info) => <Box>{info.getValue() ?? ""}</Box>,
      }),
      columnHelper.accessor("phone", {
        header: t(Resources.Clients.Phone),
        enableSorting: isSortable("phone"),
        cell: (info) => (
          <Box>{getFormattedValue(info.getValue(), undefined, phoneMask)}</Box>
        ),
      }),
      columnHelper.accessor("status", {
        header: t(Resources.Common.Status),
        enableSorting: false,
        cell: (info) => (
          <EntityStatusChip
            isActive={info.getValue() === ClientStatus.Active}
            size="small"
          />
        ),
      }),
      columnHelper.display({
        id: "action",
        header: "",
        enableSorting: false,
        meta: { justifySelf: "end" },
        cell: (info) => (
          <DetailActionCell
            path={AppRouting.ClientDetail.path(info.row.original.clientID)}
          />
        ),
      }),
    ],
    [t, isSysAdmin],
  );
}
